import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Tv, Smartphone, Laptop, Gamepad2, Printer, HardDrive } from 'lucide-react';

const productCategories = [
  {
    icon: Tv,
    title: "TVs & Displays",
    description: "LED TVs, Smart TVs, Monitors, Digital Signage",
    image: "https://images.unsplash.com/photo-1593784991095-a205069470b6?w=600&q=80",
    brands: ["Samsung", "LG", "Sony"],
    gridClass: "sm:col-span-2 sm:row-span-2"
  },
  {
    icon: Smartphone,
    title: "Mobile & Tablets",
    description: "Smartphones, Tablets, Accessories",
    image: "https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=600&q=80",
    brands: ["Apple", "Samsung", "Xiaomi", "OnePlus"],
    gridClass: "sm:col-span-1"
  },
  {
    icon: Laptop,
    title: "Laptops & Computing",
    description: "Laptops, Desktops, Workstations",
    image: "https://images.unsplash.com/photo-1496181133206-80ce9b88a853?w=600&q=80",
    brands: ["Lenovo", "HP", "MacBook", "Samsung", "Asus", "MSI"],
    gridClass: "sm:col-span-1"
  },
  {
    icon: Gamepad2,
    title: "Gaming Consoles",
    description: "PlayStation, Xbox, Gaming Accessories",
    image: "https://images.unsplash.com/photo-1606813907291-d86efa9b94db?w=600&q=80",
    brands: ["Sony", "Microsoft", "Nintendo"],
    gridClass: "sm:col-span-1 md:row-span-2"
  },
  {
    icon: Printer,
    title: "Printers & Imaging",
    description: "Inkjet, Laser, Multi-function Printers",
    image: "https://images.unsplash.com/photo-1612815154858-60aa4c59eaa6?w=600&q=80",
    brands: ["HP", "Canon", "Epson", "Toshiba", "Xerox"],
    gridClass: "sm:col-span-1"
  },
  {
    icon: HardDrive,
    title: "IT Accessories",
    description: "Hard Drives, RAM, Storage Solutions",
    image: "https://images.unsplash.com/photo-1597872200969-2b65d56bd16b?w=600&q=80",
    brands: ["Western Digital", "Seagate", "Samsung", "Adata", "Sandisk"],
    gridClass: "sm:col-span-1"
  }
];

const ProductCard = ({ product, index, isInView }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.1, duration: 0.6 }}
      className={`product-card relative group rounded-2xl overflow-hidden ${product.gridClass}`}
      data-testid={`product-card-${index}`}
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src={product.image} 
          alt={product.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-dark via-brand-dark/70 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative h-full min-h-[200px] sm:min-h-[240px] md:min-h-[280px] p-4 sm:p-6 flex flex-col justify-end">
        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-brand-accent/20 flex items-center justify-center mb-3 sm:mb-4 group-hover:bg-brand-accent/30 transition-colors">
          <product.icon className="w-5 h-5 sm:w-6 sm:h-6 text-brand-accent" />
        </div>
        
        <h3 className="font-outfit text-lg sm:text-xl md:text-2xl font-bold text-white mb-1 sm:mb-2">
          {product.title}
        </h3>
        
        <p className="text-gray-300 text-xs sm:text-sm mb-2 sm:mb-3">
          {product.description}
        </p>

        {/* Brands */}
        <div className="flex flex-wrap gap-1 sm:gap-2">
          {product.brands.map((brand) => (
            <span 
              key={brand}
              className="px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs rounded-full bg-white/10 text-gray-300"
            >
              {brand}
            </span>
          ))}
        </div>

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-brand-primary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
      </div>
    </motion.div>
  );
};

const ProductsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="products" className="py-20 md:py-32 bg-brand-surface/30" data-testid="products-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 sm:mb-16"
        >
          <span className="inline-block px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-brand-primary/20 text-brand-accent text-xs sm:text-sm font-medium mb-3 sm:mb-4">
            Our Products
          </span>
          <h2 className="font-outfit text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4">
            Premium <span className="gradient-text">Electronics & IT</span> Solutions
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-sm sm:text-base md:text-lg px-4">
            Explore our comprehensive range of consumer electronics, communication and IT products from world-leading brands
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
          {productCategories.map((product, index) => (
            <ProductCard 
              key={product.title} 
              product={product} 
              index={index}
              isInView={isInView}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;
